import React, { useState } from 'react';
import axios from 'axios';
import { AlertTriangle, BellRing, Copy, Clock, TrendingDown, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { API_BASE } from '../../config/api';

const API = `${API_BASE}/api`;

const ReasonTags = ({ learner, passMark }) => (
  <div className="flex flex-wrap gap-1.5">
    {learner.risk_reasons.includes('behind_pace') && (
      <Badge className="bg-rose-500/15 text-rose-300 text-[10px] gap-1" data-testid={`at-risk-behind-${learner.enrollment_id}`}>
        <TrendingDown className="h-3 w-3" /> Behind pace · {learner.pace_pct}% vs {passMark}%
      </Badge>
    )}
    {learner.risk_reasons.includes('inactive') && (
      <Badge className="bg-amber-500/15 text-amber-300 text-[10px] gap-1" data-testid={`at-risk-inactive-${learner.enrollment_id}`}>
        <Clock className="h-3 w-3" /> Inactive {learner.inactive_days}d
      </Badge>
    )}
  </div>
);

export default function AtRiskLearnersPanel({ learners, passMark, programmeId, token, onReminded }) {
  const [sending, setSending] = useState(null);
  const atRisk = (learners || []).filter((l) => l.at_risk);

  const remind = async (ids, key) => {
    setSending(key);
    try {
      const res = await axios.post(`${API}/programmes/${programmeId}/rubric/remind`, { enrollment_ids: ids }, { headers: { Authorization: `Bearer ${token}` } });
      const { sent, skipped } = res.data;
      if (sent) toast.success(`Reminder sent to ${sent} learner${sent > 1 ? 's' : ''}`);
      if (skipped?.length) toast.warning(`${skipped.length} learner(s) have no account yet — copy their email instead`);
      onReminded?.();
    } catch (e) {
      toast.error('Failed to send reminder');
    } finally {
      setSending(null);
    }
  };

  const copyEmail = async (email) => {
    try {
      await navigator.clipboard.writeText(email);
      toast.success('Email copied');
    } catch {
      toast.info(email);
    }
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800" data-testid="at-risk-panel">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-white text-base flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-rose-400" /> At-risk learners
          <Badge className="bg-rose-500/15 text-rose-300" data-testid="at-risk-count">{atRisk.length}</Badge>
        </CardTitle>
        {atRisk.length > 1 && (
          <Button size="sm" disabled={!!sending} onClick={() => remind(atRisk.map((l) => l.enrollment_id), 'all')} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="at-risk-remind-all-btn">
            <BellRing className="h-4 w-4 mr-2" /> Remind all
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <p className="text-xs text-zinc-500 mb-4">
          Flagged when the score is below {passMark}% of the points available so far (from started weeks and activities reached), or with no activity for 14+ days. New enrolments get a 7-day grace period for pace.
        </p>
        {!atRisk.length ? (
          <p className="text-sm text-zinc-400 flex items-center gap-2" data-testid="at-risk-empty"><CheckCircle2 className="h-4 w-4 text-green-500" /> No learners are at risk right now.</p>
        ) : (
          <div className="divide-y divide-zinc-800">
            {atRisk.map((l) => (
              <div key={l.enrollment_id} className="py-3 flex flex-wrap items-center justify-between gap-3" data-testid={`at-risk-row-${l.enrollment_id}`}>
                <div className="min-w-0 space-y-1">
                  <p className="text-sm text-white">{l.name || l.email}</p>
                  <p className="text-xs text-zinc-500">
                    {l.total_score} / {l.available_points} available pts
                    {l.last_reminded_at && ` · reminded ${new Date(l.last_reminded_at).toLocaleDateString()}`}
                  </p>
                  <ReasonTags learner={l} passMark={passMark} />
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => copyEmail(l.email)} className="border-zinc-700 text-zinc-300" data-testid={`at-risk-copy-email-${l.enrollment_id}`}>
                    <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy email
                  </Button>
                  <Button size="sm" disabled={!!sending} onClick={() => remind([l.enrollment_id], l.enrollment_id)} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid={`at-risk-remind-${l.enrollment_id}`}>
                    <BellRing className="h-3.5 w-3.5 mr-1.5" /> {sending === l.enrollment_id ? 'Sending...' : 'Send reminder'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
